import { useState } from 'react';

import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconAngleDown, IconAngleUp } from '@iwoca/icons';
import { Button, Modal, Radio, Slider } from '@iwoca/orion';
import cn from 'classnames';
import { useFormContext } from 'react-hook-form';
import { useSearchParams } from 'react-router';

import styles from './CustomersFilterDrawer.module.css';
import { displayCurrency } from '../../../../utils/DisplayCurrency';
import { TApplicationStage, TSpendingLimitStatus } from '../../Customers.types';

const STATUS_FILTER_OPTIONS = [
  { status: 'offer_approved', label: 'Ready to use' },
  { status: 'last_approved_offer', label: 'Ready to refresh' },
  { status: 'post_offer', label: 'Eligible' },
  { status: 'pre_offer', label: 'Sign-up started' },
  { status: 'declined_offer', label: 'Declined for 30 days' },
] as const satisfies ReadonlyArray<{
  status: TSpendingLimitStatus['value'];
  label: string;
}>;

const STAGE_FILTER_OPTIONS = [
  {
    stage: 'business_details',
    label: 'Business details',
  },
  {
    stage: 'security',
    label: 'Security checks',
  },
  {
    stage: 'complete',
    label: 'Completed',
  },
  {
    stage: 'declined',
    label: 'Declined',
  },
] as const satisfies ReadonlyArray<{
  stage: TApplicationStage['value'];
  label: string;
}>;

export const CustomersFilterDrawer = ({
  isOpen,
  setIsOpen,
  updateQueryParams,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  updateQueryParams: (params: {
    status: TSpendingLimitStatus['value'] | null;
    stage: TApplicationStage['value'] | null;
  }) => void;
}) => {
  const methods = useFormContext();
  const [searchParams] = useSearchParams();
  const [sliderValue, setSliderValue] = useState('0');
  const [openPaintedDoorModal, setOpenPaintedDoorModal] = useState(false);

  const handleCancel = () => {
    methods.reset({
      status: searchParams.get('status'),
      stage: searchParams.get('stage'),
    });
    setIsOpen(false);
  };

  const handleSubmit = () => {
    updateQueryParams({
      status: methods.getValues('status'),
      stage: methods.getValues('stage'),
    });
    setIsOpen(false);
  };

  return (
    <>
      {isOpen && (
        <div className="fixed right-[18rem] top-0 z-[9] h-full w-full bg-black opacity-60" />
      )}
      <Dialog static open={isOpen} onClose={handleCancel}>
        {openPaintedDoorModal && (
          <Modal
            isOpen={openPaintedDoorModal}
            onClose={() => setOpenPaintedDoorModal(false)}
            closeIcon
            headerBar={false}
          >
            <div className="py-l">
              <div className="mb-l text-2xl font-med">Watch this space</div>
              <div className="text-xl font-reg">
                Filtering by amount is coming soon
              </div>
            </div>
          </Modal>
        )}
        <DialogPanel
          className={cn(styles.outerContainer, {
            [styles.outerContainerClosed]: !isOpen,
          })}
        >
          <div className="h-full overflow-scroll p-xl">
            <div className="mb-xl flex items-center justify-between pt-m">
              <DialogTitle className="text-xl font-bold">Filters</DialogTitle>
              <button
                className="cursor-pointer border-none bg-structure-100 text-m font-bold text-primary-40"
                onClick={handleCancel}
              >
                Cancel
              </button>
            </div>
            <div className="flex flex-col gap-xl">
              <FilterDropDown title="Application stage">
                {STAGE_FILTER_OPTIONS.map((option) => (
                  <Radio
                    key={option.stage}
                    id={`checkbox__${option.label}`}
                    name="stage"
                    label={option.label}
                    value={option.stage}
                    checked={methods.watch('stage') === option.stage}
                    onClick={() => {
                      const currentStage = methods.getValues('stage');
                      const newStage =
                        currentStage === option.stage ? null : option.stage;
                      methods.setValue('stage', newStage);
                    }}
                  />
                ))}
              </FilterDropDown>
              <FilterDropDown title="Spending limit status">
                {STATUS_FILTER_OPTIONS.map((option) => (
                  <div key={option.status}>
                    <Radio
                      key={option.status}
                      id={`checkbox__${option.label}`}
                      name="status"
                      label={option.label}
                      value={option.status}
                      checked={methods.watch('status') === option.status}
                      onClick={() => {
                        const currentStatus = methods.getValues('status');
                        const newStatus =
                          currentStatus === option.status
                            ? null
                            : option.status;
                        methods.setValue('status', newStatus);
                      }}
                    />
                    {option.status === 'offer_approved' && (
                      <div className="mt-xl flex flex-col gap-xl pl-xl">
                        <Slider
                          min={0}
                          max={30_000}
                          value={sliderValue}
                          onChange={(event) => {
                            setSliderValue(event.target.value);
                          }}
                          onClick={() => {
                            tracking.ampli.customersPageAmountFilteringPaintedDoorButton();
                            setOpenPaintedDoorModal(true);
                          }}
                        />
                        <div className="flex flex-col gap-s text-xs">
                          <div className="flex justify-between text-[#737373]">
                            <div>
                              {displayCurrency(parseInt(sliderValue), 0)}
                            </div>
                            <div>{displayCurrency(30_000, 0)}</div>
                          </div>
                          <button
                            className="cursor-pointer bg-transparent p-0 text-left text-primary-40"
                            onClick={(e) => {
                              e.preventDefault();
                              setSliderValue('0');
                              tracking.ampli.customersPageAmountFilteringPaintedDoorButton();
                              setOpenPaintedDoorModal(true);
                            }}
                          >
                            Reset price range
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </FilterDropDown>
            </div>
          </div>

          <div className="p-xl shadow-black-l">
            <Button className="w-full" onClick={() => handleSubmit()}>
              Apply filters
            </Button>
          </div>
        </DialogPanel>
      </Dialog>
    </>
  );
};

const FilterDropDown = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const toggleOpen = () => {
    setIsOpen((prev) => !prev);
  };
  return (
    <div className="mb-m">
      <button
        className="flex w-full cursor-pointer items-center justify-between bg-transparent p-0 text-m"
        onClick={toggleOpen}
      >
        {title} {isOpen ? <IconAngleUp /> : <IconAngleDown />}
      </button>
      {isOpen && (
        <form className="flex flex-col gap-xl overflow-hidden pt-xl">
          {children}
        </form>
      )}
    </div>
  );
};
